import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"
import InfoIcon from "@mui/icons-material/Info"
import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material"

import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { CameraDialog } from "components/Camera/CameraDialog"
import { CriteriaInfoDialog } from "components/CriteriaInfoDialog/CriteriaInfoDialog"
import { DeleteDialog } from "components/DeleteDialog/DeleteDialog"
import { MiniGalery } from "components/MiniGalery.tsx/MiniGalery"
import {
  DeviceSetup,
  DeviceTestData,
  DeviceType,
  Examination,
  File,
  FileType,
  FileUploadInput,
  ResultType,
  TestData,
  User,
} from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, useEffect, useState } from "react"
import { emptyPerson } from "store/store"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"

type DeviceHasPassedBoxProps = {
  data: TestData
  setData: (arg: TestData) => void
  deviceSetup: DeviceSetup[]
  examination: Examination
}

type SelectedModes = {
  deviceID: string
  value: string[]
}

export const DeviceHasPassedBox = (props: DeviceHasPassedBoxProps) => {
  const { data, setData, deviceSetup, examination } = props

  const [user] = useLocalStorage<User>("user", emptyPerson)
  const [openFoto, setOpenFoto] = useState<DeviceTestData | undefined>()
  const [imageToDelete, setImageToDelete] = useState("")
  const [devicesTestData, setDevicesTestData] = useState<DeviceTestData[]>([])
  const [selectedOperatingModes, setSelectedOperatingModes] = useState<SelectedModes[]>([])

  const [selectedCriteria, setSelectedCriteria] = useState<{
    name: string
    description: any[]
  } | null>(null)

  const { uploadFile } = useStoreActions((actions) => actions)

  useEffect(() => {
    if (data.deviceTestData.length > 0) {
      const filteredDevices = data.deviceTestData.filter(
        (item) => item.device.type === DeviceType.EQUIPMENT_UNDER_TEST
      )
      setDevicesTestData(filteredDevices)

      // Extract operating modes from existing results for each device
      const extractedOperatingModes: SelectedModes[] = filteredDevices.map((deviceTestData) => {
        // Get unique operating modes from the results
        const operatingModesFromResults = Array.from(
          new Set(deviceTestData.result.map((result) => result.operatingMode))
        )

        // Get currently selected operating modes for this device (if any)
        const currentSelectedModes =
          selectedOperatingModes.find((mode) => mode.deviceID === deviceTestData.device.id)
            ?.value || []

        // Combine both: existing results + currently selected modes
        const allOperatingModes = Array.from(
          new Set([...operatingModesFromResults, ...currentSelectedModes])
        )

        return {
          deviceID: deviceTestData.device.id,
          value: allOperatingModes,
        }
      })

      setSelectedOperatingModes(extractedOperatingModes)
    } else {
      const newDeviceTestData: DeviceTestData[] = deviceSetup
        .filter((dev) => dev.device.type === DeviceType.EQUIPMENT_UNDER_TEST)
        .map((item) => {
          return {
            device: item.device,
            result: [],
            timestamp: "",
            note: "",
            images: [],
          }
        })
      setDevicesTestData(newDeviceTestData)
      // Update the parent data with the new device test data
      setData({ ...data, deviceTestData: newDeviceTestData })
    }
  }, [data.deviceTestData, deviceSetup, data, setData])

  // Handle the addition of a new photo
  const handleFoto = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }
    const formData = new FormData(event.currentTarget)

    const newImageInput: FileUploadInput = {
      id: "NEW",
      filename: v4() + ".png",
      name: formData.get("title") as string,
      description: (formData.get("description") as string) || undefined,
      base64: fotos[0],
      createdAt: new Date().toISOString(),
      createdBy: user.id,
      type: FileType.IMAGE,
    }

    const result = await uploadFile(newImageInput)

    const newImage: File = {
      id: result,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
      fileURL: "",
      name: formData.get("title") as string,
      description: (formData.get("description") as string) || undefined,
      type: FileType.IMAGE,
      filename: newImageInput.filename,
    }

    // Update both the main data and the local state
    const updatedDevices = data.deviceTestData.map((d) => {
      if (d.device.id === openFoto?.device.id) {
        return {
          ...d,
          images: [...d.images, newImage],
        }
      }
      return d
    })

    // Update the local state as well
    const updatedDevicesTestData = devicesTestData.map((d) => {
      if (d.device.id === openFoto?.device.id) {
        return {
          ...d,
          images: [...d.images, newImage],
        }
      }
      return d
    })

    setData({ ...data, deviceTestData: updatedDevices })
    setDevicesTestData(updatedDevicesTestData)
  }

  // Handle the deletion of an image
  const handleDeleteImage = () => {
    const updatedDevices = data.deviceTestData.map((item) => {
      if (item.images.some((img) => img.id === imageToDelete)) {
        return {
          ...item,
          images: item.images.filter((i) => i.id !== imageToDelete),
        }
      } else {
        return item
      }
    })

    // Update the local state as well
    const updatedDevicesTestData = devicesTestData.map((item) => {
      if (item.images.some((img) => img.id === imageToDelete)) {
        return {
          ...item,
          images: item.images.filter((i) => i.id !== imageToDelete),
        }
      } else {
        return item
      }
    })

    setData({ ...data, deviceTestData: updatedDevices })
    setDevicesTestData(updatedDevicesTestData)
    setImageToDelete("")
  }

  // Helper function to get criteria details for dialog
  const getCriteriaDetails = (criteriaCatalogId: string, criteriaId: string) => {
    const catalog = examination.passFailCriteria.find((cat) => cat.id === criteriaCatalogId)
    if (!catalog) return null

    const criteria = catalog.criteriaList.find((crit) => crit.id === criteriaId)
    return criteria
  }

  const handleInfoClick = (criteriaCatalogId: string, criteriaId: string) => {
    const criteria = getCriteriaDetails(criteriaCatalogId, criteriaId)
    if (criteria) {
      setSelectedCriteria({
        name: criteria.name,
        description: criteria.description,
      })
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {devicesTestData.map((item) => {
        return (
          <Paper sx={{ display: "flex", flexDirection: "column", p: 2, gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography>
                {item.device.barcode} - {item.device.name}
              </Typography>

              <IconButton onClick={() => setOpenFoto(item)} sx={{ p: 0 }}>
                <AddAPhotoIcon />
              </IconButton>
            </Box>
            <AutocompleteMulti
              options={examination.operatingModes}
              value={
                selectedOperatingModes.find((mode) => mode.deviceID === item.device.id)?.value || []
              }
              onChange={(_, value) => {
                // Get currently selected operating modes for this device
                const currentSelectedModes =
                  selectedOperatingModes.find((mode) => mode.deviceID === item.device.id)?.value ||
                  []

                // Find removed operating modes
                const removedModes = currentSelectedModes.filter((mode) => !value.includes(mode))

                // Update selected operating modes
                let updatedSelectedOperatingModes: SelectedModes[]
                if (selectedOperatingModes.find((mode) => mode.deviceID === item.device.id)) {
                  updatedSelectedOperatingModes = selectedOperatingModes.map((mode) => {
                    if (mode.deviceID === item.device.id) {
                      return { ...mode, value: value }
                    }
                    return mode
                  })
                } else {
                  updatedSelectedOperatingModes = [
                    ...selectedOperatingModes,
                    { deviceID: item.device.id, value: value },
                  ]
                }

                // Remove results for removed operating modes
                const updatedDevicesTestData = devicesTestData.map((d) => {
                  if (d.device.id === item.device.id) {
                    const filteredResults = d.result.filter(
                      (result) => !removedModes.includes(result.operatingMode)
                    )
                    return {
                      ...d,
                      result: filteredResults,
                    }
                  }
                  return d
                })

                setSelectedOperatingModes(updatedSelectedOperatingModes)
                setDevicesTestData(updatedDevicesTestData)
                setData({ ...data, deviceTestData: updatedDevicesTestData })
              }}
              label="Betriebsmodi"
              fullWidth
              isEnum={true}
            />

            <Box>
              {examination.passFailCriteria.map((criteriaCatalog) => (
                <Box key={criteriaCatalog.id}>
                  {(() => {
                    const selectedModes =
                      selectedOperatingModes.find((mode) => mode.deviceID === item.device.id)
                        ?.value || []

                    // Erstelle eine flache Liste von Criteria mit ihren zugehörigen Betriebsmodi
                    const criteriaWithModes = criteriaCatalog.criteriaList.flatMap((criteria) => {
                      return criteria.operatingModes
                        .filter((mode) => selectedModes.includes(mode))
                        .map((mode) => ({ criteria, operatingMode: mode }))
                    })

                    return criteriaWithModes.map(({ criteria, operatingMode }, index) => (
                      <Box
                        key={`${criteria.id}-${operatingMode}-${index}`}
                        sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            size="small"
                            onClick={() => handleInfoClick(criteriaCatalog.id, criteria.id)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>

                          <Typography variant="subtitle2">
                            {criteria.name} - {operatingMode}
                          </Typography>
                        </Box>
                        <Box sx={{ ml: 1 }}>
                          <RadioGroup
                            row
                            value={
                              item.result.find(
                                (r) =>
                                  r.criteria === criteria.id &&
                                  r.criteriaCatalog === criteriaCatalog.id &&
                                  r.operatingMode === operatingMode
                              )?.type || ResultType.NOT_EVALUATED
                            }
                            onChange={(e) => {
                              const updatedDevicesTestData = devicesTestData.map((d) => {
                                if (d.device.id === item.device.id) {
                                  // Entferne das bestehende Result für diese Kombination
                                  const filteredResults = d.result.filter(
                                    (r) =>
                                      !(
                                        r.criteria === criteria.id &&
                                        r.criteriaCatalog === criteriaCatalog.id &&
                                        r.operatingMode === operatingMode
                                      )
                                  )

                                  // Füge das neue Result hinzu
                                  const newResult = {
                                    criteria: criteria.id,
                                    criteriaCatalog: criteriaCatalog.id,
                                    operatingMode: operatingMode,
                                    type: e.target.value as ResultType,
                                  }

                                  return {
                                    ...d,
                                    result: [...filteredResults, newResult],
                                  }
                                }
                                return d
                              })

                              setData({ ...data, deviceTestData: updatedDevicesTestData })
                              setDevicesTestData(updatedDevicesTestData)
                            }}
                          >
                            <FormControlLabel
                              value={ResultType.PASSED}
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">Bestanden</Typography>}
                            />
                            <FormControlLabel
                              value={ResultType.FAILED}
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">Nicht bestanden</Typography>}
                            />
                            <FormControlLabel
                              value={ResultType.NOT_EVALUATED}
                              control={<Radio size="small" />}
                              label={<Typography variant="body2">Nicht bewertet</Typography>}
                            />
                          </RadioGroup>
                        </Box>
                        <Divider />
                      </Box>
                    ))
                  })()}
                </Box>
              ))}
            </Box>

            <TextField
              fullWidth
              multiline
              size="small"
              label="Kommentar"
              value={item.note}
              onChange={(e) => {
                const updatedDevicesTestData = devicesTestData.map((d) => {
                  if (d.device.id === item.device.id) {
                    return {
                      ...d,
                      note: e.target.value,
                    }
                  }
                  return d
                })
                setData({ ...data, deviceTestData: updatedDevicesTestData })
                setDevicesTestData(updatedDevicesTestData)
              }}
            />

            {item.images && item.images.length > 0 && (
              <Box>
                <MiniGalery images={item.images} onDelete={setImageToDelete} />
              </Box>
            )}
          </Paper>
        )
      })}

      <CameraDialog
        open={openFoto !== undefined}
        onClose={() => setOpenFoto(undefined)}
        onSave={handleFoto}
        imageName={openFoto?.device.barcode}
      />

      <DeleteDialog
        open={imageToDelete !== ""}
        onClose={() => setImageToDelete("")}
        onDelete={handleDeleteImage}
      />

      <CriteriaInfoDialog
        open={selectedCriteria !== null}
        onClose={() => setSelectedCriteria(null)}
        criteriaName={selectedCriteria?.name || ""}
        description={selectedCriteria?.description || []}
      />
    </Box>
  )
}
